import {NatApplicationRepo} from '../../repository/NatApplicationRepo'
import NatCondominiumGroupRepo from '../../../condominiumGroup/repository/NatCondominiumGroupRepo'
import NatCurrentDateTimeRepo from '../../../currentDateTime/repository/NatCurrentDateTimeRepo'
import EventEmitterExAdmin from '../../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {Location} from 'react-router-dom'
import {CondominiumGroupData, NatGetCondominiumGroup} from '../../../condominiumGroup/repository/NatGetCondominiumGroup'
import {Nullable} from '../../../../../app/TypeHelpers'
import {
    SearchResult,
    UserRegistrationApplyInfo,
    UserRegistrationApplySearchCondition
} from '../../repository/NatGetUserRegistrationApplyList'
import {SortOrder} from '../../../../../components/shared/SortOrder'
import React, {Component, ReactElement, ReactNode} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../../../components/shared/hoc/createAdminPage'
import {SessionStorageHelper} from '../../../../../SessionStorageHelper'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import {RESIDENT_APPLY_STATUS} from '../../../../../CodeMaster'
import {COMMON_CONSTANTS} from '../../../../../CommonConstants'
import ListSearchFormArea from '../../../../../styledComponents/adminPage/ListSearchFormArea'
import {PageMainArea} from '../../../../../components/shared/hoc/withAdminPageTemplate'
import styled from 'styled-components'
import SortableTableHeader, {
    SortableTableHeaderCell
} from '../../../../../styledComponents/adminPage/SortablePaginationTableHeader'
import SortablePaginationTable from '../../../../../styledComponents/adminPage/SortablePaginationTable'
import SortablePaginationTableBody from '../../../../../styledComponents/adminPage/SortablePaginationTableBody'
import {convertToRoomNumberForResident} from '../../../../common/util/Natutil'
import {SearchResultCountDisplayProps} from '../../../../../styledComponents/adminPage/SearchResultCountDisplay'
import {Messages} from '../../../../../Messages'
import {Box, Link, SortDirection, TableCell, TableRow} from '@mui/material';
import {
    FullWidthLeftRowFlexContainer,
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    RightRowFlexContainer
} from '../../../../../styledComponents/FlexComponents'
import NatCondominiumGroupSelectComponent from '../../../common/NatCondominiumGroupSelectComponent'
import PPTextField from '../../../../../components/shared/forms/PPTextField'
import {AdminDefaultMediumButton} from '../../../../../styledComponents/adminPage/Buttons'
import PPCheckboxGroup from '../../../../../components/shared/forms/PPCheckboxGroup'

const KEY_USER_REGISTRATION_APPLY_LIST_SEARCH_CONDITION: string = 'userRegistrationApplyListSearchCondition'

interface Props {
    applicationRepo: NatApplicationRepo
    condominiumGroupRepo: NatCondominiumGroupRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumGroup: Nullable<CondominiumGroupData>
    userRegistrationApplyList: UserRegistrationApplyInfo[]
    searchResult: SearchResult
    searchCondition: UserRegistrationApplySearchCondition
    searchConditionSortInfo: SortOrder[]
    defaultApplyStatus: CheckBoxData[]
    isLoadCompleted: boolean
}

export interface CheckBoxData {
    id: string
    label: string
    disp: boolean
}

class NatAdminUserRegistrationApplyListPage extends Component<Props & AdminPagePropsSet, States> {
    subSortKey: SortOrder = new SortOrder('applyId', 'asc')

    constructor(props: Props & AdminPagePropsSet) {
        super(props)

        // 検索条件の初期設定
        let defaultSearchCondition: UserRegistrationApplySearchCondition = {}
        let lastSearchCondition: UserRegistrationApplySearchCondition = {}

        // 前回検索条件が保持されていればデフォルト値として設定する
        const sessionStorageSearchCondition: Nullable<string> =
            SessionStorageHelper.getStringItemOrNull(KEY_USER_REGISTRATION_APPLY_LIST_SEARCH_CONDITION)

        if (sessionStorageSearchCondition) {
            lastSearchCondition = JSON.parse(sessionStorageSearchCondition)
        } else {
            // 前回検索条件が保持されていない場合、申請ステータスは「受付待ち」で絞り込む
            const defaultStatus: string[] = []
            defaultStatus.push(RESIDENT_APPLY_STATUS.RECEPTION_WAITING.value)
            defaultSearchCondition.applyStatus = defaultStatus.join(',')
        }

        if (lastSearchCondition.pageNum) {
            lastSearchCondition.pageNum = Number(lastSearchCondition.pageNum)
            lastSearchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        } else {
            defaultSearchCondition.pageNum = COMMON_CONSTANTS.ROWS_PER_PAGE_SETTINGS[2]
            defaultSearchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        }

        const loginUserCondominiumGroupId = NatAdminLoginInfoSessionStorageHelper.getNumberItemOrNull(
            AdminLoginInfoSessionStorageKey.LOGIN_USER_CONDOMINIUM_GROUP_ID)

        if (!defaultSearchCondition.condominiumGroupId &&
            loginUserCondominiumGroupId) {
            defaultSearchCondition.condominiumGroupId = loginUserCondominiumGroupId
        }

        if (Object.keys(lastSearchCondition).length) {
            defaultSearchCondition = {...lastSearchCondition}
        }

        this.state = {
            condominiumGroup: null,
            userRegistrationApplyList: [],
            searchCondition: defaultSearchCondition,
            searchConditionSortInfo: [
                new SortOrder('applyStatus', 'asc'),
                new SortOrder('applyDatetime', 'asc'),
            ],
            defaultApplyStatus: [],
            searchResult: {
                total: 0,
                totalPage: 0,
                start: 0,
                end: 0
            },
            isLoadCompleted: false,
        }

        this.onChangeCondominiumGroupId = this.onChangeCondominiumGroupId.bind(this)
        this.onChangeCondominiumCd = this.onChangeCondominiumCd.bind(this)
        this.onChangeCondominiumName = this.onChangeCondominiumName.bind(this)
        this.changeApplyId = this.changeApplyId.bind(this)
        this.onClickSearchButton = this.onClickSearchButton.bind(this)
        this.handleSortApplication = this.handleSortApplication.bind(this)
        this.handleChangePage = this.handleChangePage.bind(this)
        this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const condominiumGroupResponse = await this.getCondominiumGroup()

            this.setState({
                condominiumGroup: condominiumGroupResponse
            })

            // ユーザ登録申請一覧取得
            await this.getUserRegistrationApplyList(
                this.state.searchCondition,
                this.state.searchConditionSortInfo,
            )
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getCondominiumGroup(): Promise<Nullable<CondominiumGroupData>> {
        const condominiumGroupResponse: Nullable<NatGetCondominiumGroup> = await this.props.asyncCallFunction(
            this.props.condominiumGroupRepo.getCondominiumGroup.bind(this.props.condominiumGroupRepo),
            (httpErrorOrNetworkError) => {
                this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
            },
        )
        return condominiumGroupResponse?.details ?? null
    }

    async getUserRegistrationApplyList(
        searchCondition: UserRegistrationApplySearchCondition,
        searchConditionSortInfo: SortOrder[],
    ): Promise<void> {
        await this.props.eventEmitter.showLoading()

        try {
            const userRegistrationApplyListResponse = await this.props.asyncCallFunction(
                this.props.applicationRepo.getUserRegistrationApplyList.bind(
                    this.props.applicationRepo,
                    searchCondition, [...searchConditionSortInfo, this.subSortKey]),
                (httpErrorOrNetworkError) => {
                    this.props.eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                },
            )

            if (userRegistrationApplyListResponse?.details) {
                this.setState({
                    userRegistrationApplyList: userRegistrationApplyListResponse.details.applyInfo,
                    searchResult: userRegistrationApplyListResponse.details.searchResult,
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    getSortDirection(sortKey: string): SortDirection {
        const sortOrder: SortOrder | undefined =
            this.state.searchConditionSortInfo.find(order => order.sortKey === sortKey)
        if (sortOrder === undefined) {
            return false
        }
        return sortOrder.sortDirection
    }

    getDispApplyStatus(applyStatusCode: string): string {
        const applyStatus = RESIDENT_APPLY_STATUS.codeToEnumConstants(applyStatusCode)
        if (applyStatus) {
            return applyStatus.label
        } else {
            return ''
        }
    }

    async handleChangePage(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
        newPage: number,
    ): Promise<void> {
        this.beforeUserAction()
        const searchCondition: UserRegistrationApplySearchCondition = this.state.searchCondition
        searchCondition.pageNum = Number(this.state.searchCondition.pageNum)
        searchCondition.page = newPage
        await this.getUserRegistrationApplyList(
            this.state.searchCondition,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchCondition: searchCondition})
    }

    async handleSortApplication(sortKey: string): Promise<void> {
        this.beforeUserAction()

        let sortDirection: SortDirection = this.getSortDirection(sortKey)
        if (sortDirection === false || sortDirection === 'desc') {
            sortDirection = 'asc'
        } else {
            sortDirection = 'desc'
        }

        const sortOrders: SortOrder[] = [new SortOrder(sortKey, sortDirection)]
        await this.getUserRegistrationApplyList(
            this.state.searchCondition,
            sortOrders,
        )
        this.setState({searchConditionSortInfo: sortOrders})
    }

    async handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
        this.beforeUserAction()

        // 検索条件を保持
        const lastSearchCondition: UserRegistrationApplySearchCondition = {
            condominiumGroupId: this.state.searchCondition.condominiumGroupId,
            condominiumCd: this.state.searchCondition.condominiumCd,
            condominiumName: this.state.searchCondition.condominiumName,
            applyStatus: this.state.searchCondition.applyStatus,
            applyId: this.state.searchCondition.applyId,
            pageNum: Number(event.target.value)
        }
        SessionStorageHelper.setItem(
            KEY_USER_REGISTRATION_APPLY_LIST_SEARCH_CONDITION,
            JSON.stringify(lastSearchCondition)
        )

        const searchCondition: UserRegistrationApplySearchCondition = this.state.searchCondition
        searchCondition.pageNum = Number(event.target.value)
        searchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        await this.getUserRegistrationApplyList(
            this.state.searchCondition,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchCondition: searchCondition})
    }

    renderUserRegistrationApplyRecord(userRegistrationApply: UserRegistrationApplyInfo): ReactElement {
        const userRegistrationApplyUrl: string = `/admin/condominiums/${userRegistrationApply.condominiumCd}/applications/userRegistrationApply/${userRegistrationApply.applyId}`

        return (
            <TableRow key={userRegistrationApply.applyId}>
                <TableCell style={{width: '240px', textAlign: 'center'}}>
                    <ApplyStatusCell status={userRegistrationApply.applyStatus} style={{width: '180px'}}>
                        {this.getDispApplyStatus(userRegistrationApply.applyStatus)}
                    </ApplyStatusCell>
                </TableCell>
                <TableCell style={{width: '175px'}}>
                    <Link href={userRegistrationApplyUrl}>{userRegistrationApply.applyId}</Link>
                </TableCell>
                <TableCell style={{width: '320px'}}>
                    {userRegistrationApply.condominiumName}
                </TableCell>
                <TableCell style={{width: '180px'}}>
                    {`${userRegistrationApply.ridgeName} ${convertToRoomNumberForResident(userRegistrationApply.roomNumber)}`}
                </TableCell>
                <TableCellWordBreakAll style={{width: '240px'}}>
                    {`${userRegistrationApply.applicantFamilyName} ${userRegistrationApply.applicantFirstName}`}
                </TableCellWordBreakAll>
                <TableCell style={{width: '200px'}}>
                    {userRegistrationApply.applyDatetime}
                </TableCell>
            </TableRow>
        )
    }

    renderUserRegistrationApplyTable(
        userRegistrationApplyList: UserRegistrationApplyInfo[]): Nullable<ReactElement> {
        if (userRegistrationApplyList.length === 0) {
            return null
        }
        let headers: SortableTableHeaderCell[]

        headers = [
            {id: 'applyStatus', label: 'ステータス', align: 'center',
                sortable: true, direction: this.getSortDirection('applyStatus'),},
            {id: 'applyId', label: '申請ID', align: 'left'},
            {id: 'condominiumName', label: '物件名', align: 'left'},
            {id: 'roomNumber', label: '部屋番号', align: 'left'},
            {id: 'applicantName', label: '申請者氏名', align: 'left'},
            {id: 'applyDatetime', label: '申請日時', align: 'left',
                sortable: true, direction: this.getSortDirection('applyDatetime'),
            },
        ]

        return (
            <SortablePaginationTable
                paginationProps={{
                    count: this.state.searchResult.total ? this.state.searchResult.total : 0,
                    rowsPerPage:
                        this.state.searchCondition.pageNum ? this.state.searchCondition.pageNum : 0,
                    page: this.state.searchCondition.page ? this.state.searchCondition.page : 0,
                    onPageChange: this.handleChangePage,
                    onRowsPerPageChange: this.handleChangeRowsPerPage,
                }}
            >
                <SortableTableHeader
                    noWrap
                    headers={headers}
                    onSortOrderChange={this.handleSortApplication}
                />
                <SortablePaginationTableBody>
                    {this.state.userRegistrationApplyList.map(
                        userRegistrationApply =>
                            this.renderUserRegistrationApplyRecord(userRegistrationApply)
                    )}
                </SortablePaginationTableBody>
            </SortablePaginationTable>
        )
    }

    searchResults(): SearchResultCountDisplayProps {
        if (this.state.userRegistrationApplyList.length === 0) {
            return {noResultMessage: Messages.IOPCC0014.toString()}
        }

        return {
            totalCount: this.state.searchResult.total ? this.state.searchResult.total : undefined,
            rowsPerPage: this.state.searchCondition.pageNum,
            page: this.state.searchCondition.page,
        }
    }

    beforeUserAction(): void {
        this.props.eventEmitter.clearHeadingAreaMessage()
    }

    async onClickSearchButton(): Promise<void> {
        this.beforeUserAction()
        // 検索条件を保持
        const lastSearchCondition: UserRegistrationApplySearchCondition = {
            condominiumGroupId: this.state.searchCondition.condominiumGroupId,
            condominiumCd: this.state.searchCondition.condominiumCd,
            condominiumName: this.state.searchCondition.condominiumName,
            applyStatus: this.state.searchCondition.applyStatus,
            applyId: this.state.searchCondition.applyId,
            pageNum: this.state.searchCondition.pageNum
        }
        SessionStorageHelper.setItem(
            KEY_USER_REGISTRATION_APPLY_LIST_SEARCH_CONDITION,
            JSON.stringify(lastSearchCondition)
        )

        const searchCondition = this.state.searchCondition
        searchCondition.page = COMMON_CONSTANTS.FIRST_PAGE
        searchCondition.pageNum = Number(this.state.searchCondition.pageNum)
        await this.getUserRegistrationApplyList(
            searchCondition,
            this.state.searchConditionSortInfo,
        )
        this.setState({searchCondition: searchCondition})
    }

    onChangeCondominiumGroupId(condominiumGroupId: number): void {
        const searchConditions = this.state.searchCondition
        searchConditions.condominiumGroupId = condominiumGroupId
        this.setState({searchCondition: searchConditions})
    }

    onChangeCondominiumCd(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchConditions = this.state.searchCondition
        searchConditions.condominiumCd = event.target.value
        this.setState({searchCondition: searchConditions})
    }

    onChangeCondominiumName(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchConditions = this.state.searchCondition
        searchConditions.condominiumName = event.target.value
        this.setState({searchCondition: searchConditions})
    }

    judgeCheckStatus(applyStatus: string): boolean {
        if (!this.state.searchCondition || !this.state.searchCondition.applyStatus) {
            return false
        }
        const roleArray: string[] = this.state.searchCondition.applyStatus.split(',')
        return roleArray.indexOf(applyStatus) >= 0
    }

    clickApplyStatus(label: string, checked: boolean): void {

        const checkedStatus = RESIDENT_APPLY_STATUS.values().filter(role => {
            return role.label === label
        })

        if (this.state.searchCondition.applyStatus) {
            const checkedStatusArray: string[] = this.state.searchCondition.applyStatus.split(',')
            if (checked) {
                checkedStatusArray.push(checkedStatus[0].value)
            } else {
                checkedStatusArray.splice(checkedStatusArray.indexOf(checkedStatus[0].value), 1)
            }
            const searchCondition = this.state.searchCondition
            searchCondition.applyStatus = checkedStatusArray.join(',')
            this.setState({searchCondition: searchCondition})
        } else {
            if (checked) {
                const searchCondition = this.state.searchCondition
                searchCondition.applyStatus = checkedStatus[0].value
                this.setState({searchCondition: searchCondition})
            }
        }
    }

    renderApplyStatusCheckBox(): ReactNode {
        const labels: { label: string, checked: boolean }[] = []
        const applyStatusMaster = RESIDENT_APPLY_STATUS.values()
            .filter(applyStatus => applyStatus !== RESIDENT_APPLY_STATUS.ASSOC_DIRECTOR_WAITING
                && applyStatus !== RESIDENT_APPLY_STATUS.PET_CHAIRMAN_WAITING)

        applyStatusMaster
            .map((applyStatus) => (
                labels.push(
                    {
                        label: applyStatus.label,
                        checked: this.judgeCheckStatus(applyStatus.value),
                    },
                )
            ))

        return (
            <StyledPPCheckboxGroup
                row
                data={labels}
                onChangeChecked={this.clickApplyStatus.bind(this)}
            />
        )
    }

    changeApplyId(event: React.ChangeEvent<HTMLInputElement>): void {
        const searchCondition: UserRegistrationApplySearchCondition = this.state.searchCondition
        searchCondition.applyId = event.target.value
        this.setState({searchCondition: searchCondition})
    }

    renderUserRegistrationApplySearchInfo(): ReactNode {
        return (
            <>
                <Box pl={'8px'} pt={'8px'}>
                    <LeftRowFlexContainer>
                        <SearchCategoryColumnContainer>物件情報</SearchCategoryColumnContainer>
                        <Box width={'126px'}>物件グループを選択</Box>
                        <SearchInputContainer>
                            <LeftColumnFlexContainer>
                                <Box width={'500px'} ml={'8px'}>
                                    {this.state.condominiumGroup &&
                                        <NatCondominiumGroupSelectComponent
                                            condominiumGroup={this.state.condominiumGroup}
                                            onChangeSelectedCondominiumGroupId={this.onChangeCondominiumGroupId}
                                            defaultValue={(this.state.searchCondition.condominiumGroupId) ?
                                                this.state.searchCondition.condominiumGroupId : null}
                                            requiredFlag={true}
                                            eventKeys={['condominiumGroupId']}
                                            eventEmitter={this.props.eventEmitter}
                                            autoFocus={true}
                                        />
                                    }
                                </Box>
                            </LeftColumnFlexContainer>
                        </SearchInputContainer>
                    </LeftRowFlexContainer>
                    <SearchRowContainer>
                        <SearchCategoryColumnContainer/>
                        <SearchColumnContainerForAll>物件コード</SearchColumnContainerForAll>
                        <SearchInputContainer>
                            <Box width={'300px'}>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 6,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.onChangeCondominiumCd}
                                    defaultValue={this.state.searchCondition.condominiumCd}
                                />
                            </Box>
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <SearchRowContainer>
                        <SearchCategoryColumnContainer/>
                        <SearchColumnContainerForAll>物件名</SearchColumnContainerForAll>
                        <SearchInputContainer>
                            <Box width={'300px'}>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 50,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.onChangeCondominiumName}
                                    defaultValue={this.state.searchCondition.condominiumName}
                                />
                            </Box>
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <SearchRowContainer>
                        <SearchCategoryColumnContainer>申請情報</SearchCategoryColumnContainer>
                        <SearchColumnContainerForAll>ステータス</SearchColumnContainerForAll>
                        <SearchInputContainer>
                            {this.renderApplyStatusCheckBox()}
                        </SearchInputContainer>
                    </SearchRowContainer>
                    <LeftRowFlexContainer>
                        <SearchCategoryColumnContainer/>
                        <SearchColumnContainerForAll>申請ID</SearchColumnContainerForAll>
                        <SearchInputContainer>
                            <Box width={'280px'}>
                                <PPTextField
                                    InputProps={{
                                        inputProps: {
                                            maxLength: 8,
                                        },
                                    }}
                                    fullWidth
                                    onChange={this.changeApplyId}
                                    defaultValue={this.state.searchCondition.applyId}
                                    eventKeys={['applyId']}
                                    eventEmitter={this.props.eventEmitter}
                                />
                            </Box>
                        </SearchInputContainer>
                        <Box flex={1} ml={'16px'}>
                            <RightRowFlexContainer>
                                <SearchButton onClick={this.onClickSearchButton}>
                                    検索する
                                </SearchButton>
                            </RightRowFlexContainer>
                        </Box>
                    </LeftRowFlexContainer>
                </Box>
            </>
        )
    }

    render(): Nullable<ReactElement> {
        if (!this.state.isLoadCompleted) {
            return null
        }

        return <>
            <ListSearchFormArea
                searchResult={this.searchResults()}
                onEnterKey={this.onClickSearchButton}>
                {this.renderUserRegistrationApplySearchInfo()}
            </ListSearchFormArea>
            <PageMainArea>
                {this.renderUserRegistrationApplyTable(this.state.userRegistrationApplyList)}
            </PageMainArea>
        </>
    }
}

export default createAdminPage(NatAdminUserRegistrationApplyListPage)

const ApplyStatusCell = styled.button<{ status?: string }>`
  width: 200px;
  height: 29px;
  border: 0;
  border-radius: 40px;
  background: ${props => props.status === '8' ? 'linear-gradient(0deg, rgba(0, 175, 185, 0.4), rgba(0, 175, 185, 0.4)), #FFFFFF;' :
    props.status === '9' ? 'linear-gradient(0deg, #EDEDED, #EDEDED), #FFFFFF;' :
        'linear-gradient(0deg, rgba(255, 191, 86, 0.4), rgba(255, 191, 86, 0.4)), #FFFFFF;'}
`

const SearchButton = styled(AdminDefaultMediumButton)`
  &.MuiButtonBase-root {
    width: 204px;
    height: 44px;
  }
`

const TableCellWordBreakAll = styled(TableCell)`
  &.MuiTableCell-root {
    word-break: break-all;
  }
`

const SearchRowContainer = styled(FullWidthLeftRowFlexContainer)`
  padding: 8px 0;
`

const SearchCategoryColumnContainer = styled.div`
  font-weight: bold;
  width: 56px;
  margin-right: 24px;
  flex-shrink: 0;
`

const StyledPPCheckboxGroup = styled(PPCheckboxGroup)`
  & .MuiTypography-root {
    font-size: 14px;
  }
`

const SearchColumnContainerForAll = styled.div`
  width: 104px;
  flex-shrink: 0;
`

const SearchInputContainer = styled.div`
  margin-left: 16px;
`
